import React from 'react';
import { EMPRESA, LEGAL_LAST_UPDATE } from '../../lib/legal';

// Política de Privacidad — Chile (Ley 21.719 nueva + Ley 19.628 vigente hasta transición).
// Contenido revisable por asesor legal. Formato claro y en primera persona.

const H = ({ children }) => (
  <h3 className="text-base font-bold text-slate-900 mt-6 mb-2">{children}</h3>
);

const Privacidad = () => (
  <div data-testid="privacidad-content">
    <p className="text-xs text-slate-500 uppercase tracking-wider">
      Última actualización: {LEGAL_LAST_UPDATE}
    </p>
    <p>
      En <strong>PreventiApp</strong> (marca operada por <strong>{EMPRESA.razonSocial}</strong>,
      RUT {EMPRESA.rut}, domiciliada en {EMPRESA.direccion}) protegemos tus datos personales y
      los de tu comunidad educativa. Esta política explica qué datos tratamos, con qué finalidad
      y cómo puedes ejercer tus derechos, en cumplimiento de la Ley N° 19.628 sobre Protección
      de la Vida Privada y la Ley N° 21.719 de Protección de Datos Personales de Chile.
    </p>

    <H>1. Responsable del tratamiento</H>
    <p>
      {EMPRESA.razonSocial} · RUT {EMPRESA.rut}. Domicilio: {EMPRESA.direccion}. Contacto para
      asuntos de privacidad: <a href={`mailto:${EMPRESA.emailPrivacidad}`} className="text-brand-violet underline">
      {EMPRESA.emailPrivacidad}</a>.
    </p>

    <H>2. Datos que recolectamos</H>
    <ul className="list-disc pl-5 space-y-1">
      <li><strong>Formulario de contacto:</strong> nombre, cargo, colegio, correo, teléfono,
        comuna, mensaje y motivo de contacto.</li>
      <li><strong>Cookies y datos técnicos:</strong> dirección IP, tipo de navegador, páginas
        visitadas y tiempo de permanencia (sólo si aceptas la analítica).</li>
      <li><strong>Plataforma SaaS (app.preventiapp.com):</strong> credenciales, perfil de
        usuario, colegio, observaciones registradas por el establecimiento y evidencias
        anonimizadas. El colegio actúa como <em>responsable</em> y PreventiApp como
        <em> encargado del tratamiento</em>.</li>
    </ul>

    <H>3. Finalidades</H>
    <ul className="list-disc pl-5 space-y-1">
      <li>Responder solicitudes de contacto, demo o cotización.</li>
      <li>Prestar el servicio SaaS a establecimientos educacionales suscritos.</li>
      <li>Mejorar la experiencia del sitio web mediante analítica agregada.</li>
      <li>Cumplir obligaciones legales, tributarias y contractuales.</li>
    </ul>

    <H>4. Base de licitud</H>
    <p>
      Tratamos tus datos con base en (a) tu <strong>consentimiento</strong> expreso al
      completar formularios o aceptar cookies, (b) la <strong>ejecución del contrato</strong> con
      los establecimientos educacionales que contratan la plataforma, y (c) el
      <strong> cumplimiento de obligaciones legales</strong> aplicables.
    </p>

    <H>5. Almacenamiento y transferencias internacionales</H>
    <p>
      Los datos se alojan en proveedores de infraestructura como servicios en la nube — entre
      ellos <strong>Supabase, Firebase, MongoDB Atlas</strong> y otros servicios utilizados por
      PreventiApp — con respaldo internacional en <strong>Estados Unidos de América</strong>.
      Estos proveedores cumplen con estándares reconocidos de seguridad (SOC 2, ISO 27001) y
      se rigen por sus propias políticas de protección de datos.
    </p>
    <p>
      Al usar el servicio consientes esta transferencia internacional en los términos del
      artículo 27 de la Ley N° 21.719.
    </p>

    <H>6. Tiempo de retención</H>
    <ul className="list-disc pl-5 space-y-1">
      <li>Datos del formulario de contacto: hasta 24 meses o hasta que solicites su eliminación.</li>
      <li>Datos operativos del SaaS: mientras dure la suscripción del establecimiento + 12
        meses de respaldo, salvo obligación legal de conservación mayor.</li>
      <li>Cookies analíticas: hasta 12 meses.</li>
    </ul>

    <H>7. Seguridad</H>
    <p>
      Aplicamos medidas técnicas y organizativas apropiadas: cifrado TLS en tránsito, cifrado
      en reposo del proveedor, control de acceso por roles (RLS a nivel de base de datos),
      registro de auditoría, anonimización obligatoria de rostros en evidencias fotográficas
      (doble capa: cliente + servidor), y backups automáticos.
    </p>

    <H>8. Tus derechos (ARCOP+)</H>
    <p>
      Puedes ejercer en cualquier momento tus derechos de <strong>Acceso, Rectificación,
      Cancelación, Oposición y Portabilidad</strong> escribiendo a{' '}
      <a href={`mailto:${EMPRESA.emailPrivacidad}`} className="text-brand-violet underline">
      {EMPRESA.emailPrivacidad}</a>. Responderemos en un plazo máximo de 30 días corridos.
      También puedes reclamar ante la Agencia de Protección de Datos Personales cuando ésta
      entre en funciones conforme a la Ley 21.719.
    </p>

    <H>9. Uso de cookies</H>
    <p>
      Usamos cookies <strong>esenciales</strong> (imprescindibles para el funcionamiento del
      sitio) y, con tu consentimiento explícito, cookies de <strong>analítica</strong>
      (Google Analytics / Tag Manager) y de <strong>marketing</strong>. Puedes cambiar tu
      preferencia en cualquier momento borrando las cookies del sitio o escribiendo a
      privacidad.
    </p>

    <H>10. Menores de edad</H>
    <p>
      La plataforma trata datos de niños, niñas y adolescentes en el contexto escolar. El
      tratamiento se realiza con la autorización de los establecimientos educacionales
      responsables y respetando el interés superior del niño según Ley 21.430. Los apoderados
      pueden ejercer los derechos ARCOP+ en representación del menor.
    </p>

    <H>11. Cambios a esta política</H>
    <p>
      Podemos actualizar esta política para reflejar cambios legales o del servicio.
      Notificaremos actualizaciones relevantes por email o en el sitio. La versión vigente
      siempre estará publicada en <a href={EMPRESA.sitio} className="text-brand-violet underline">
      {EMPRESA.sitio}</a>.
    </p>

    <H>12. Contacto</H>
    <p>
      Delegado / responsable de privacidad:{' '}
      <a href={`mailto:${EMPRESA.emailPrivacidad}`} className="text-brand-violet underline">
      {EMPRESA.emailPrivacidad}</a><br />
      Consultas generales:{' '}
      <a href={`mailto:${EMPRESA.emailGeneral}`} className="text-brand-violet underline">
      {EMPRESA.emailGeneral}</a>
    </p>
  </div>
);

export default Privacidad;
